import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import UploadProvider from "@components_v2/upload-provider/upload-provider";
import UploadAssetItem from "@components_v2/upload-asset-item/upload-asset-item";
import SelectFolderPathModal from "@components/modals/SelectFolderPath";
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Tooltip,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconCancel, IconClose, IconMetadata, IconOpenInNew, IconResetSmall, IconSchema } from "@mzc-cloudplex/icons";
import uniqid from "uniqid";
import TooltipButton from "../../components_v2/tooltip-button/tooltip-button";
import TagsInput from "../../features/tags-input/tags-input";
import { StyledGrid } from "@components_v2/grid/grid.styled";
import {
    CATEGORY_TYPES,
    COMMON_JOB_STATUS,
    CONSTANTS,
    JOB_PANEL_TABS,
    LIMIT_BYTE,
    LIMIT_CHAR,
    MEDIA_TYPE_EXTENSIONS,
    MEDIA_TYPES,
    UPLOAD_FILE_LOCATION_TYPE,
    UPLOAD_LOCATION,
    UPLOAD_STATUS,
} from "@constants";
import compareVersions, {
    VERSION_CM_11209_ADD_CUSTOM_METADATA,
    VERSION_CM_14175_ASSET_LOCK_WHILE_CREATE,
    VERSION_CM_14991_ASSET_GROUPED_BY_MEDIA_TYPE_SETTINGS,
    VERSION_CM_15200_PROJECT_MULTI_ACCOUNT,
} from "@cores/version";
import { getAssetCreateRequestMessage, getAssetMultiCreateRequestMessage } from "@cores/notification/message";
import { setNotification } from "@modules/notification";
import { getExtensionFromName, getMediaTypeFromExtension, getMediaTypeFromMimeType } from "@lib/mime/mime";
import DialogSelectS3Files from "@features/dialog/dialog-select-s3-files";
import DialogSelectS3FilesV2 from "@features/dialog/dialog-select-s3-files-v2";
import { alpha } from "@mui/material";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import DialogSelectCollection from "@features/dialog/dialog-select-collection";
import TableCard from "../../components_v2/card/table-card";
import CollectionThumbnail from "../../features/thumbnail/collection-thumbnail";
import Categories from "@routes/categories";
import AttributionInputArea from "../../components/inputArea/AttributionInputArea";
import {
    addAttribution,
    createAsset as createAssetData,
    removeAttribution,
    reset as createAssetReset,
    setValidationError,
    updateAttribution,
    updateData,
    updateState,
} from "@modules/createAsset";
import { bulkCreateAssetElements, createOwnerLock } from "@modules/asset";
import {
    makeUploadQueueJob,
    retryUploadQueueJob,
    uploadFilesFinished,
    uploadJobFiles,
    uploadJobFileSetStatus,
} from "@modules/uploadQueue";
import { clearMetadataTemplate, getDetailMetadataTemplate, getMetadataTemplate } from "@modules/metadataTemplate";
import MetadataFieldEditMode from "@features/metadata-fields/metadata-field-edit-mode";
import MetadataSkeletonCreateAsset from "@routes/metadata-skeleton-create-asset";
import tryParse from "@modules/settingParser";
import { getContentSchemaDetail } from "@modules/content-schema";
import useCustomContent from "@hooks/useCustomContent";
import useMyJobs from "@hooks/useMyJobs";
import { getDriveData } from "@modules/folderTreeView";
import validateInput from "@cores/validateInput";
import TextField from "@components_v2/text-field/text-field";
import useSettings from "@hooks/useSettings";
import PanelPropertyTitle from "@components/view/components/panel-property-title";
import { getContentSchemasAPI } from "@modules/apis/content-schemas";
import StickyHeaderDialog from "@components_v2/dialog/sticky-header-dialog";
import FolderLocation from "@components_v2/folder-location/folder-location";
import RecentFolderLocation from "@components_v2/recent-folder-location/recent-folder-location";
import { getCategoryGroupAPI } from "@modules/categoryGroupsManagement";
import { getCategoryAPI } from "@modules/categoriesManagement";
import VersionedComponent from "@components/VersionedComponent";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import DialogSelectCustomMetadata from "@features/dialog/custom-metadata/dialog-select-custom-metadata";

import { isLocal } from "@cores/enviromentExtension";
import menuService from "@services/menuService";
import { useLocation } from "react-router";

const LOCATION_TYPE = UPLOAD_LOCATION.CREATE_ASSET;
const CollectionColumn = [
    {
        id: "name",
        label: "Name",
        style: { custom: { width: "calc(100% - 126px)" } },
        disabled: true,
        checked: true,
        sortable: false,
        useInAutocomplete: true,
    },
    {
        id: "count",
        label: "Assets Count",
        style: { custom: { width: 54 } },
        disabled: false,
        checked: true,
        sortable: false,
        useInAutocomplete: true,
    },
];

const LX_PURCHASE_FIELD_ID = "jWyYPagVkBnZ3VOp2bw7qDGL";
const LX_UAS_FIELD_ID = "PbV4RZWel5ODxONoJ8jBG172"; //PbV4RZWel5ODxONoJ8jBG172

export const DialogCreateAsset = ({
    open,
    assetItems,
    initialFolder,
    initialCategory,
    initialCustomMetadata,
    onClose,
    onRefresh,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);
    const { getCustomMetadataByContentSchema, checkIsNull } = useCustomContent();
    const { setLocalStorageRecentLocation } = useVirtualFolderPath({
        domain: "ASSET",
    });
    const { createJobData, updateMyJobsData, updateMyJobsTaskData, openMyJobs } = useMyJobs({
        resource: "createAsset",
        store: "uploadQueue",
    });
    const { domainStatus, getSettingData } = useSettings({ type: "DomainStatus" });
    const { getVirtualFolderFullPathList } = useVirtualFolderPath({ domain: "ASSET" });

    const [destination, setDestination] = useState({
        fixedPrefix: "",
        fullPath: "",
    });
    const [fileDialogOpen, setFileDialogOpen] = useState(false);
    const [pendingCreate, setPendingCreate] = useState(false);
    const [modal, setModal] = useState();
    const [folderLocation, setFolderLocation] = useState([]);

    const [collection, setCollection] = useState();
    const [customMetadataSchema, setCustomMetadataSchema] = useState({
        data: [],
        pending: false,
        defaultCustomMetadataId: null,
    });
    const isLxHausys = window.location.hostname.includes("lxhausys.");
    const isAtomy = spaceId === "atomy-singapore";
    const [categoryGroupTooltipDisabled, setCategoryGroupTooltipDisabled] = useState(false);
    const [categoryGroup, setCategoryGroup] = useState(null);
    const [isOneAsset, setIsOneAsset] = useState(!isAtomy);
    const [pendingMetadata, setPendingMetadata] = useState(true);

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const createAsset = useSelector(({ createAsset }) => createAsset);
    const uploadQueue = useSelector(({ uploadQueue }) => uploadQueue);
    const folderTreeView = useSelector(({ folderTreeView }) => folderTreeView);

    const { metadataTemplate, detailMetadataTemplate, defaultMetadataId } = useSelector(
        ({ metadataTemplate, detailMetadataTemplate, defaultMetadataId }) => ({
            metadataTemplate: metadataTemplate.metadataTemplateList,
            detailMetadataTemplate: metadataTemplate.detailMetadataTemplate,
            defaultMetadataId: metadataTemplate.defaultMetadataId,
        }),
    );

    const REGEXP_SCHEME = new RegExp("^(s3|https)://", "i");

    const collections = useSelector(({ collections }) => collections.create);

    const validateSelectedFile = useCallback((file) => {
        const mediaType = getMediaTypeFromExtension(getExtensionFromName(file.name));
        const acceptFileExtension = MEDIA_TYPE_EXTENSIONS[mediaType];

        if (!file) {
            return null;
        }
        if (!acceptFileExtension.includes("*")) {
            const REGEXP_EXTENSION = new RegExp(`.(${acceptFileExtension.join("|")})$`, "i");
            if (!REGEXP_EXTENSION.test(file.name)) {
                return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_EXTENSION;
            }
        }

        if (file.url && !REGEXP_SCHEME.test(file.url)) {
            return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_URL_PATTERN;
        }

        if (file.size <= 0) {
            return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").INVALID_FILE;
        }

        return null;
    }, []);

    const tryGetMimeType = (file) => {
        const { name } = file;
        if (name.endsWith(".exr")) {
            return "image/x-exr";
        }
        if (name.endsWith(".tga")) {
            return "image/x-tga";
        }
        return "application/octet-stream";
    };

    // Blob URL들을 revoke하는 유틸리티 함수
    const revokeBlobUrls = useCallback(() => {
        createAsset.originFiles?.forEach((file) => {
            if (file.accessUrl && file.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL) {
                URL.revokeObjectURL(file.accessUrl);
            }
        });
    }, [createAsset.originFiles]);

    const onHandleChangeFiles = (files) => {
        const selectedFiles = files;
        if (!selectedFiles || Object.keys(selectedFiles).length === 0) {
            return;
        }

        const count = createAsset.originFiles.length;
        addSelectedFiles(
            Object.keys(selectedFiles).reduce((acc, key, currentIndex) => {
                const selectedFile = selectedFiles[key];

                selectedFile.key = String(count + currentIndex);
                selectedFile.displayName = `${createAsset.data?.name ?? ""}${selectedFile.name.slice(
                    0,
                    selectedFile.name.lastIndexOf("."),
                )}`;
                selectedFile.mimeType =
                    selectedFile.type?.length > 0 ? selectedFile.type : tryGetMimeType(selectedFile);
                selectedFile.locationType = selectedFile.locationType ?? UPLOAD_FILE_LOCATION_TYPE.LOCAL;
                selectedFile.validationError = validateSelectedFile(selectedFile);

                if (
                    selectedFile.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL &&
                    ["IMAGE"].includes(getMediaTypeFromMimeType(selectedFile.type))
                ) {
                    selectedFile.accessUrl = URL.createObjectURL(selectedFile);
                }

                acc.push(selectedFile);
                return acc;
            }, []),
        );
    };

    const addSelectedFiles = (selectedFiles) => {
        dispatch(updateState({ originFiles: [...createAsset.originFiles, ...selectedFiles] }));
    };

    const onClickCreateAsset = async () => {
        await createAssetAsync(createAsset);
    };

    const onHandleFolderPath = ({ folderId, folderPath }) => {
        const fLocation = folderPath?.map((item) => {
            return { name: item.name };
        });
        dispatch(updateData({ virtualFolder: { id: folderId } }));
        setFolderLocation(fLocation);
        setModal("");
    };

    const initContentSchemaMetadata = (contentSchema) => {
        dispatch(updateData({ contentSchemaMetadata: contentSchema }));
    };

    const onHandleChangeCustomMetadata = async (id, value, validationErrors) => {
        const metadata = [...createAsset.data.contentSchemaMetadata.fields];
        metadata?.forEach((meta) => {
            if (meta.id === id) {
                meta.value = value;
            }
            if ((isLocal || isLxHausys) && meta.id === LX_PURCHASE_FIELD_ID) {
                const isPurchaseSelected = value === "구매";
                const uasField = metadata.find((field) => field.id === LX_UAS_FIELD_ID);
                if (uasField) {
                    uasField.isRequired = isPurchaseSelected;
                }
            }
        });
        dispatch(
            updateData({
                contentSchemaMetadata: { ...createAsset.data.contentSchemaMetadata, fields: metadata },
            }),
        );
        dispatch(setValidationError({ key: `custom-metadata-${id}`, error: validationErrors }));
    };

    const assetCustomMetadata = useMemo(
        () =>
            createAsset.data.contentSchemaMetadata?.fields?.map((meta) => ({
                ...meta,
                onChangeField: onHandleChangeCustomMetadata,
            })),
        [createAsset.data.contentSchemaMetadata, onHandleChangeCustomMetadata],
    );

    const onChangeRecentLocation = ({ locationId, locations }) => {
        dispatch(updateData({ virtualFolder: { id: locationId } }));
        setFolderLocation(locations);
    };

    const updateRecentLocation = useCallback(() => {
        const folderId = createAsset.data?.virtualFolder?.id;
        setLocalStorageRecentLocation({
            folderId,
        });
    }, [createAsset.data, folderLocation]);

    const loadDetailMetadata = async (metadataTemplateId) => {
        if (!metadataTemplateId) return;

        const detailMetadataTemplate = await dispatch(
            getDetailMetadataTemplate({
                templateId: metadataTemplateId,
            }),
        );
        setCategoryGroup(detailMetadataTemplate.template.categoryGroup);
        dispatch(
            updateData({
                name: detailMetadataTemplate.template.namePrefix,
                categories: detailMetadataTemplate.template.categories,
                attributions: detailMetadataTemplate.template.attributions?.map((attr, index) => ({
                    ...attr,
                    id: index,
                })),
                tags: detailMetadataTemplate.template.tags?.map((tag) => tag.value),
            }),
        );
    };

    const checkAndUploadFiles = async (uploadQueue, locationType, jobKey, mediaType, elements) => {
        const targets = uploadQueue[locationType].filter((item) => item.key === jobKey)[0];

        const selectedFiles = targets?.files;

        const filePut = async (selectedFile) => {
            return await dispatch(
                uploadJobFiles({
                    locationType,
                    id: selectedFile.id,
                    key: jobKey,
                    uploadCompleteCallback: () => {},
                    type: "Asset",
                }),
            );
        };

        const fileAlreadyUploaded = async (selectedFile) => {
            return await dispatch(
                uploadJobFileSetStatus({
                    locationType,
                    id: selectedFile.id,
                    key: jobKey,
                    status: "COMPLETE",
                    uploadCompleteCallback: () => {},
                }),
            );
        };

        let status = "SUCCEED";

        for (let i = 0; i < selectedFiles?.length; i++) {
            const selectedFile = selectedFiles[i];
            if (selectedFile.status === "COMPLETE") {
                fileAlreadyUploaded(selectedFile);
                continue;
            }

            const filePutResponse = await filePut(selectedFile);
            status = filePutResponse.status;

            const element = elements[i];
            if (element) {
                if (element.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL) {
                    element.displayName = selectedFiles[i].displayName.split(".")[0];
                    mediaType === MEDIA_TYPES.VIDEO || mediaType === MEDIA_TYPES.AUDIO
                        ? (element.urls = [filePutResponse.url])
                        : (element.url = filePutResponse.url);
                }
            }

            if (status === "CANCELED") {
                return "CANCELED";
            } else if (status === "ERROR") {
                return "ERROR";
            }
        }

        return "COMPLETE";
    };

    const retryUpload = async ({ key, mediaType, elements }) => {
        const locationType = LOCATION_TYPE;
        const { retryKey, uploadQueue } = await dispatch(retryUploadQueueJob({ locationType, key }));

        const results = await checkAndUploadFiles(uploadQueue, locationType, retryKey, mediaType, elements);

        if (results === "SUCCEED" || results === "COMPLETE") {
            dispatch(uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.COMPLETE }));
        } else if (results === "CANCELED") {
            dispatch(uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.CANCELED }));
        } else if (results === "ERROR") {
            dispatch(uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.ERROR }));
            // this.toastJustMessage('error', `Upload error.`)
        }

        // jobs note: 각 상태에 myJobs 값을 변경해주는 함수 호출 필요함(status, partialSuccess, error message? => 반환을 해주나)

        return results;
    };

    const createAssetAsync = useCallback(
        async (createAsset) => {
            if (pendingCreate) return;
            let jobId = null;

            try {
                setPendingCreate(true);

                let virtualFolderId = createAsset.data?.virtualFolder?.id;
                if (!virtualFolderId) {
                    if (folderTreeView?.driveList?.data?.length > 0) {
                        virtualFolderId = folderTreeView?.driveList?.data?.find(
                            (el) => el.resourceType?.type === "ASSET",
                        )?.[0]?.rootFolder?.id;
                    } else {
                        const driveList = await dispatch(
                            getDriveData({
                                headerParams: { projectId: project.id },
                                domain: "ASSET",
                            }),
                        );
                        virtualFolderId = driveList?.[0]?.rootFolder?.id;
                    }
                }

                //job 더미데이터 생성은 1depth의 경우 use hook에서 만들어서 반환해주는 걸로
                jobId = createJobData({
                    action: "UPLOAD_ASSET",
                    metadata: {
                        folderId: virtualFolderId,
                    },
                    taskCount: createAsset.elementsByMediaType.length,
                    isFolded: true,
                }).jobId;

                let count = 0;
                const element = createAsset.elementsByMediaType;
                const elementKey = element.length > 0 ? element[0].key : null;

                element?.map(() => {
                    count += createAsset.selectedFiles[elementKey].length;
                });
                if (count > 0) count -= 1;
                const name =
                    createAsset.selectedFiles[elementKey].length > 0
                        ? createAsset.selectedFiles[elementKey][0]?.displayName
                        : "";

                showRequestMessage(name, count);
                onHandleCloseAddAsset();
                updateRecentLocation();
                setPendingCreate(false);
                openMyJobs(JOB_PANEL_TABS.ASSET_JOBS);

                let partialFailure = false;

                await Promise.allSettled(
                    createAsset.elementsByMediaType?.map(async (groupElement, i) => {
                        try {
                            const { key, ...newGroupElement } = groupElement;

                            const groupElementName =
                                createAsset.selectedFiles[key].length > 0
                                    ? createAsset.selectedFiles[key][0]?.displayName
                                    : "";

                            let elementsOfS3File = createAsset.elements[key]?.filter(
                                (el) => el.locationType === UPLOAD_FILE_LOCATION_TYPE.S3,
                            );

                            let elementsOfLocalFile =
                                createAsset.elements[key]?.filter(
                                    (el) => el.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL,
                                ) ?? [];

                            elementsOfS3File?.map((e) => {
                                delete e.key;
                                delete e.name;
                                if (
                                    groupElement.mediaType === MEDIA_TYPES.VIDEO ||
                                    groupElement.mediaType === MEDIA_TYPES.AUDIO
                                ) {
                                    e.displayName = e.urls[0].match(/([^/]+)(?=\.\w+$)/)[0];
                                } else {
                                    e.displayName = e.url.match(/([^/]+)(?=\.\w+$)/)[0];
                                }
                            });

                            const parsedAssetData = {
                                ...createAsset.data,
                                ...newGroupElement,
                                name: groupElementName,
                                extendedMetadata: {
                                    contentSchemaId: createAsset.data.contentSchemaMetadata?.id,
                                    data: {
                                        fields: createAsset.data.contentSchemaMetadata?.fields
                                            ?.filter((el) => el.isEditable && !checkIsNull(el.value))
                                            ?.map((meta) => ({
                                                id: meta.id,
                                                value: meta.value,
                                            })),
                                    },
                                },
                                elements: elementsOfS3File,
                            };

                            const response = await dispatch(createAssetData(parsedAssetData, false));

                            if (elementsOfLocalFile.length) {
                                if (i === 0) updateMyJobsData({ jobId, data: { status: COMMON_JOB_STATUS.RUNNING } });

                                const createdAssetId = response.data.id;
                                const { version } = response.data;

                                const selectedFiles = createAsset.selectedFiles[key].filter(
                                    (el) => el.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL,
                                );

                                // elements에도 업로드 URL 반영
                                elementsOfLocalFile?.map((e, i) => {
                                    delete e.key;
                                    delete e.name;
                                });

                                const files = selectedFiles?.map((item) => {
                                    item.id = uniqid();
                                    return item;
                                });

                                const { name, key: newGroupElementKey, ...rest } = newGroupElement;
                                const jobKey = await dispatch(
                                    makeUploadQueueJob({
                                        locationType: LOCATION_TYPE,
                                        files,
                                        resourceId: createdAssetId,
                                        data: {
                                            job: { jobId },
                                            ...createAsset.data,
                                            elements: elementsOfLocalFile,
                                            version,
                                            id: createdAssetId,
                                            name: name,
                                            metadata: { ...rest },
                                        },
                                    }),
                                );

                                const result = await retryUpload({
                                    key: jobKey,
                                    mediaType: groupElement.mediaType,
                                    elements: elementsOfLocalFile,
                                });

                                if (result === "SUCCEED" || result === "COMPLETE") {
                                    await dispatch(
                                        bulkCreateAssetElements(createdAssetId, version, {
                                            elements: elementsOfLocalFile,
                                        }),
                                    );
                                } else if (result === "ERROR") {
                                    throw new Error("error");
                                }
                            }
                            if (createAsset.data.isOwnerLocked) {
                                const params = { assetIds: [response.data.id] };
                                await dispatch(createOwnerLock({ isLocked: false, params }));
                            }
                        } catch (e) {
                            partialFailure = true;
                            updateMyJobsTaskData({ jobId, taskIndex: i, data: { status: UPLOAD_STATUS.ERROR } });
                            console.log(e);
                            throw e;
                        }
                    }),
                );

                updateMyJobsData({
                    jobId,
                    data: { status: UPLOAD_STATUS.COMPLETE, endAt: new Date().toISOString(), partialFailure },
                });
            } catch (error) {
                updateMyJobsData({
                    jobId,
                    data: { status: UPLOAD_STATUS.COMPLETE, endAt: new Date().toISOString(), partialFailure: true },
                });
                console.error(`error`, error);
                setPendingCreate(false);
            }
        },
        [pendingCreate, createAsset, isOneAsset, stage, uploadQueue],
    );

    const showRequestMessage = (name, count) => {
        dispatch(
            setNotification({
                type: "info",
                contents:
                    count > 0
                        ? getAssetMultiCreateRequestMessage({ name, count })
                        : getAssetCreateRequestMessage({ name }),
            }),
        );
    };

    const onHandleCloseModal = () => {
        setModal("");
    };

    const onHandleCloseAddAsset = () => {
        onClose();
    };

    const onHandleSubmitCollection = (collection) => {
        setCollection(collection);
        onHandleCloseModal();
    };

    const onChangeCategories = (options) => {
        dispatch(
            updateData({
                categories: options.categories,
            }),
        );
        if (options.categoryGroup) {
            setCategoryGroupTooltipDisabled(categoryGroup?.id === options.categoryGroup?.id);
            setCategoryGroup({ id: options.categoryGroup.id, name: options.categoryGroup.label });
        }
    };

    const onChangeTags = (tags) => {
        dispatch(
            updateData({
                tags: tags,
            }),
        );
    };

    const onChangeAttributionValue = (id, value, validationError) => {
        dispatch(
            setValidationError({
                key: `attribution-${id}-value`,
                error: validationError,
            }),
        );

        dispatch(
            updateAttribution({
                id,
                value,
            }),
        );
    };

    const onChangeAttributionKey = (id, key, validationError) => {
        dispatch(
            setValidationError({
                key: `attribution-${id}-key`,
                error: validationError,
            }),
        );

        dispatch(
            updateAttribution({
                id,
                key,
            }),
        );
    };

    const onClickAddAttribution = (id, validationError) => {
        dispatch(addAttribution(id));

        dispatch(
            setValidationError({
                key: `attribution-${id}-key`,
                error: validationError,
            }),
        );

        dispatch(
            setValidationError({
                key: `attribution-${id}-value`,
                error: validationError,
            }),
        );
    };

    const onClickRemoveAttribution = (id) => {
        dispatch(removeAttribution(id));

        dispatch(
            setValidationError({
                key: `attribution-${id}-key`,
                error: null,
            }),
        );

        dispatch(
            setValidationError({
                key: `attribution-${id}-value`,
                error: null,
            }),
        );
    };

    const onChangeDescription = (description) => {
        dispatch(
            updateData({
                description,
            }),
        );

        dispatch(
            setValidationError({
                key: `description`,
                error: validateInput(description, { maxLength: LIMIT_CHAR.description }),
            }),
        );
    };

    const customMetaDataRequiredError = useMemo(() => {
        if (!createAsset.data.contentSchemaMetadata) return false;
        return createAsset.data.contentSchemaMetadata.fields.some((el) => el.validationError && checkIsNull(el.value));
    }, [createAsset.data.contentSchemaMetadata]);

    const getCollectionTableValueByColumn = useCallback((columnId, option) => {
        if (columnId === "name")
            return (
                <TableCard
                    thumbnail={<CollectionThumbnail collection={option} size={"small"} variant={"table"} />}
                    name={{
                        name: (
                            <Typography variant={"body1"} component={"span"}>
                                {option?.name}
                            </Typography>
                        ),
                    }}
                />
            );
        if (columnId === "count")
            return (
                <Grid item>
                    <BadgeCollection totalCount={option?.assetTotalCount} />
                </Grid>
            );
        return null;
    }, []);

    const fileCount = useMemo(() => {
        let count = 0;
        if (Object.keys(createAsset.selectedFiles)?.length === 0) return count;
        Object.keys(createAsset.selectedFiles)?.map((el) => (count += createAsset.elements[el]?.length));
        return count;
    }, [createAsset]);

    const handleInitialFolder = async (initialFolder) => {
        if (initialFolder) {
            if (!initialFolder.folderPath) {
                try {
                    initialFolder.folderPath = await getVirtualFolderFullPathList({
                        folderId: initialFolder.folderId,
                        includeFolder: true,
                    });
                } catch (error) {
                    console.error("Failed to load folder path:", error);
                    return;
                }
            }
            onHandleFolderPath({ folderId: initialFolder.folderId, folderPath: initialFolder.folderPath });
        }
    };

    const handleInitialCategory = async (initialCategory) => {
        setPendingMetadata(true);

        if (!initialCategory) {
            loadDefaultMetadata();
        } else {
            await dispatch(getMetadataTemplate({ domain: "ASSET", excludeDefaultMetadata: true }));

            if (initialCategory?.categoryGroupName && initialCategory?.categoryName) {
                setCategoryGroup({
                    id: initialCategory.categoryGroupId,
                    name: initialCategory.categoryGroupName,
                });
                dispatch(
                    updateData({
                        categories: [
                            {
                                id: initialCategory.categoryId,
                                name: initialCategory.categoryName,
                            },
                        ],
                    }),
                );
            } else {
                try {
                    const { data: categoryGroupData } = await getCategoryGroupAPI(
                        stage.id,
                        stage.endpoint,
                        project.id,
                        initialCategory.categoryGroupId,
                    );
                    const { data: categoryData } = await getCategoryAPI(
                        stage.id,
                        stage.endpoint,
                        project.id,
                        initialCategory.categoryGroupId,
                        initialCategory.categoryId,
                    );

                    setCategoryGroup({ id: categoryGroupData.id, name: categoryGroupData.name });
                    dispatch(updateData({ categories: [categoryData] }));
                } catch (error) {
                    console.error("Failed to load category:", error);
                }
            }
        }

        setPendingMetadata(false);
    };

    const handleInitialCustomMetadata = async (initialCustomMetadata, resourceSettings) => {
        setCustomMetadataSchema({ ...customMetadataSchema, pending: true });
        const customMetadataParams = { type: "EXTENDED_METADATA", status: "ACTIVE" };
        const customMetadataData = await getContentSchemasAPI(
            stage.id,
            stage.endpoint,
            project.id,
            customMetadataParams,
        );
        let defaultCustomMetadataId = null;
        if (!initialCustomMetadata) {
            defaultCustomMetadataId = await loadDefaultCustomMetadata(resourceSettings);
        } else {
            if (initialCustomMetadata?.customMetadataId) {
                loadDetailCustomMetadata(initialCustomMetadata.customMetadataId);
            }
        }
        setCustomMetadataSchema({
            ...customMetadataSchema,
            data: customMetadataData.data,
            defaultCustomMetadataId,
            pending: false,
        });
    };

    const loadDefaultMetadata = async () => {
        try {
            setPendingMetadata(true);
            const response = await dispatch(getMetadataTemplate({ domain: "ASSET" }));
            const { defaultMetadataId } = response;
            if (defaultMetadataId) await loadDetailMetadata(defaultMetadataId);
        } catch (e) {
            console.log(e);
        }
    };

    const loadDetailCustomMetadata = async (customMetadataId) => {
        try {
            const customMetadata = await dispatch(getContentSchemaDetail(customMetadataId, project.id));
            const parsedCustomMetadata = getCustomMetadataByContentSchema(customMetadata);
            initContentSchemaMetadata(parsedCustomMetadata);
        } catch (error) {
            console.error("Failed to load default custom metadata:", error);
        }
    };

    const loadDefaultCustomMetadata = async (resourceSettings) => {
        try {
            let defaultCustomMetadataId = null;

            if (resourceSettings?.Asset?.defaultCustomMetadata?.value) {
                const defaultCustomMetadata = tryParse(resourceSettings, "Asset", "defaultCustomMetadata", 2, true);
                defaultCustomMetadataId = defaultCustomMetadata?.value?.id;
                if (defaultCustomMetadataId) {
                    await loadDetailCustomMetadata(defaultCustomMetadataId);
                }
            }
            return defaultCustomMetadataId;
        } catch (e) {
            console.log(e);
        }
    };

    const init = async () => {
        const resourceSettings = await getSettingData("Asset");

        handleInitialFolder(initialFolder);
        handleInitialCategory(initialCategory);
        handleInitialCustomMetadata(initialCustomMetadata, resourceSettings);
        if (compareVersions(stage.version, VERSION_CM_14991_ASSET_GROUPED_BY_MEDIA_TYPE_SETTINGS) >= 0)
            setIsOneAsset(resourceSettings?.Asset?.isAssetGroupedByType?.value !== "false");
    };

    useEffect(() => {
        init();

        return () => {
            revokeBlobUrls();
            setFolderLocation([]);
            dispatch(createAssetReset());
            dispatch(clearMetadataTemplate());
        };
    }, []);

    useEffect(() => {
        dispatch(
            updateData({
                collection: collection ? { id: collection?.id } : {},
            }),
        );
    }, [collection, dispatch]);

    const shouldShowUasLinkButton = useCallback(() => {
        if (!assetCustomMetadata) {
            return false;
        }
        if (!isLxHausys) {
            return false;
        }
        const purchaseField = assetCustomMetadata.find((field) => field.id === LX_PURCHASE_FIELD_ID);
        const uasField = assetCustomMetadata.find((field) => field.id === LX_UAS_FIELD_ID);
        if (purchaseField && uasField) {
            const isPurchaseSelected = purchaseField.value === "구매";
            if (isPurchaseSelected) {
                return true;
            }
        }
        return false;
    }, [assetCustomMetadata]);

    return (
        <>
            {modal === "selectFolderPath" && (
                <SelectFolderPathModal
                    open
                    domain={"Asset"}
                    submitText={`Select`}
                    width={"600px"}
                    title={"Select location"}
                    subFoldersOption={false}
                    useNewFolder={false}
                    onSubmit={onHandleFolderPath}
                    onClose={onHandleCloseModal}
                />
            )}
            {modal === "selectS3Files" &&
                (compareVersions(stage.version, VERSION_CM_15200_PROJECT_MULTI_ACCOUNT) >= 0 ? (
                    <DialogSelectS3FilesV2
                        open={modal === "selectS3Files"}
                        onSubmit={onHandleChangeFiles}
                        onClose={onHandleCloseModal}
                        isMultiFileSelect={true}
                    />
                ) : (
                    <DialogSelectS3Files
                        open={modal === "selectS3Files"}
                        onSubmit={onHandleChangeFiles}
                        onClose={onHandleCloseModal}
                        onlyLocal={""}
                        maximumSize={"5 GB"}
                        acceptFileExtension={"*"}
                        isMultiFileSelect={true}
                    />
                ))}
            {modal === "selectCollection" && (
                <DialogSelectCollection
                    open={modal === "selectCollection"}
                    title={"Specify Collection"}
                    selectLabel={"Specify Here"}
                    placeholder={t("common::msg::Please search by collection name.")}
                    onSubmit={onHandleSubmitCollection}
                    actionPending={false}
                    onClose={onHandleCloseModal}
                    getValueByColumn={getCollectionTableValueByColumn}
                    isDisabledCheckbox={true}
                />
            )}
            {modal === "selectCustomMetadata" && (
                <DialogSelectCustomMetadata
                    domain={"ASSET"}
                    onClickClose={onHandleCloseModal}
                    onClickSubmit={(contentSchema) => {
                        initContentSchemaMetadata(contentSchema);
                        setModal("");
                    }}
                />
            )}
            <StickyHeaderDialog
                onClose={onHandleCloseAddAsset}
                open={open}
                variant="expandable"
                sx={{ "& .MuiDialog-paper": { maxWidth: "960px" } }}
            >
                <DialogTitle>
                    <Grid container>
                        <Typography variant="h3">{t(`common::label::Create`)}</Typography>
                    </Grid>
                    <TooltipButton
                        circled
                        title={t(`common::label::Close`)}
                        onClickButton={() => onHandleCloseAddAsset()}
                        buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                    />
                </DialogTitle>
                <DialogContent sx={{ "&&": { pr: "10px" } }}>
                    <Box>
                        <Grid
                            container
                            sx={{
                                width: "100%",
                                maxWidth: "1232px",
                                minHeight: "400px",
                                display: "grid",
                                gridTemplateRows: "auto 1fr",
                                pb: 2.5,
                            }}
                        >
                            <UploadProvider
                                open={fileDialogOpen}
                                onChangeFiles={onHandleChangeFiles}
                                disabled={pendingCreate}
                                disabledClick={true}
                                showDragSpacer={true}
                                isMultiFileSelect={true}
                            >
                                <Grid item>
                                    <Button
                                        variant={"outlined"}
                                        color={"primary"}
                                        rounded={true}
                                        onClick={() => setModal("selectS3Files")}
                                        size={"small"}
                                    >
                                        {t(`common::label::Upload from cloud`, `Upload from cloud`)}
                                    </Button>
                                </Grid>
                            </UploadProvider>

                            <Grid container sx={{ gridArea: "2/1/3/2" }} gap={3}>
                                <Grid container alignItems="start" flexDirection={`column`} gap={1.5}>
                                    <StyledGrid
                                        item
                                        theme={theme}
                                        usePadding={fileCount > 0}
                                        useBorder={fileCount > 0}
                                        sx={{ mt: fileCount > 0 ? 3 : 0 }}
                                    >
                                        <UploadAssetItem isOneAsset={isOneAsset} disabled={pendingCreate} />

                                        {/*콜렉션 지정 영역*/}
                                        {fileCount > 0 && (
                                            <>
                                                <Divider sx={{ mt: 3, mb: 2 }} />
                                                <Grid container alignItems="center" width={"100%"}>
                                                    {collection && (
                                                        <Grid
                                                            container
                                                            flexDirection={"row"}
                                                            alignItems={"center"}
                                                            sx={{
                                                                px: 1.5,
                                                                py: 1,
                                                                backgroundColor: alpha(
                                                                    theme.palette.greySecondary[50],
                                                                    0.5,
                                                                ),
                                                                borderRadius: "4px",
                                                                mb: 2,
                                                            }}
                                                        >
                                                            {CollectionColumn?.map((col) => (
                                                                <Grid
                                                                    item
                                                                    {...col.style?.custom}
                                                                    key={`dialog-create-asset-collection-${col?.id}`}
                                                                >
                                                                    {getCollectionTableValueByColumn(
                                                                        col?.id,
                                                                        collection,
                                                                    )}
                                                                </Grid>
                                                            ))}
                                                            <Grid item mx={2.5}>
                                                                <IconButton
                                                                    size="medium"
                                                                    circled
                                                                    onClick={(e) => setCollection(null)}
                                                                >
                                                                    <IconCancel
                                                                        size={12}
                                                                        htmlColor={theme.palette.grey[700]}
                                                                    />
                                                                </IconButton>
                                                            </Grid>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                                <Button
                                                    animation={"wave"}
                                                    animationtarget="endIcon"
                                                    animationtype="toRight"
                                                    color="secondary"
                                                    endIcon={<IconOpenInNew size={16} />}
                                                    label="Button"
                                                    onClick={() => setModal("selectCollection")}
                                                    size="medium"
                                                    type="button"
                                                    variant="outlined"
                                                    sx={{ float: "inline-end" }}
                                                >
                                                    {t(`common::label::Specify Collection`, `Specify Collection`)}
                                                </Button>
                                            </>
                                        )}
                                    </StyledGrid>
                                    {fileCount > 0 && (
                                        <Grid container flexDirection={"row"} justifyContent={"flex-end"}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        color={"secondary"}
                                                        checked={!isOneAsset}
                                                        onClick={(e) => {
                                                            setIsOneAsset(!isOneAsset);
                                                        }}
                                                    />
                                                }
                                                label={t(
                                                    `common::label::Create assets from all files individually`,
                                                    `Create assets from all files individually`,
                                                )}
                                                sx={{ ml: 1, mr: 0 }}
                                            />
                                        </Grid>
                                    )}
                                </Grid>

                                {/*Location 영역*/}
                                <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                    <Typography
                                        variant={`body1`}
                                        component={"p"}
                                        color={theme.palette.secondary[800]}
                                        required={true}
                                    >
                                        {t(`common::label::Location`)}
                                    </Typography>
                                    <Divider orientation="vertical" flexItem />
                                    <StyledGrid item theme={theme}>
                                        <Grid container flexDirection={"row"} gap={2} alignItems={"center"}>
                                            <Grid item sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                                                <FolderLocation
                                                    folderLocation={folderLocation}
                                                    onClickChange={() => setModal("selectFolderPath")}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <RecentFolderLocation
                                                    domain={"ASSET"}
                                                    onChangeRecentLocation={onChangeRecentLocation}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    disabled={folderLocation?.length === 0}
                                                    size={"small"}
                                                    variant={"text"}
                                                    startIcon={<IconResetSmall size={16} />}
                                                    color={"greySecondary"}
                                                    onClick={() =>
                                                        initialFolder
                                                            ? handleInitialFolder(initialFolder)
                                                            : setFolderLocation([])
                                                    }
                                                >
                                                    <Typography variant={"body3"}>
                                                        {t(`common::label::Reset`)}
                                                    </Typography>
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </StyledGrid>
                                </Grid>

                                {/*Metadata 영역*/}
                                <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                    <Grid container flexDirection={"row"} alignItems={"center"}>
                                        <Grid item flex={1}>
                                            <Typography
                                                variant={`body1`}
                                                component={"p"}
                                                color={theme.palette.secondary[800]}
                                            >
                                                {t(`common::label::Metadata`)}
                                            </Typography>
                                        </Grid>
                                        <Select
                                            variant={"filled"}
                                            displayEmpty
                                            onChange={(e) => loadDetailMetadata(e.target.value)}
                                            sx={{
                                                backgroundColor: theme.palette.common.white,
                                            }}
                                            value={detailMetadataTemplate.data?.id || defaultMetadataId}
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return (
                                                        <Grid
                                                            container
                                                            flexDirection={"row"}
                                                            gap={1}
                                                            alignItems="center"
                                                        >
                                                            <IconMetadata size={16} />
                                                            <Typography
                                                                variant={"body3"}
                                                                color={theme.palette.secondary[800]}
                                                            >
                                                                {t(`common::label::Load Metadata`)}
                                                            </Typography>
                                                        </Grid>
                                                    );
                                                }

                                                const selectedTemplate = metadataTemplate?.data?.find(
                                                    (template) => template.id === selected,
                                                );

                                                return (
                                                    <Grid container flexDirection={"row"} gap={1} alignItems="center">
                                                        <IconMetadata size={16} color={theme.palette.secondary[700]} />
                                                        <Typography variant="body1">
                                                            {selectedTemplate?.name}
                                                        </Typography>
                                                    </Grid>
                                                );
                                            }}
                                        >
                                            {metadataTemplate?.pending ? (
                                                <MenuItem>
                                                    <Grid container flexDirection={"column"} px={1.5} py={0.5} gap={1}>
                                                        {new Array(3).fill("").map((_, i) => (
                                                            <Grid item key={`metadata-template-skeleton-${i}`}>
                                                                <Skeleton
                                                                    variant={"rounded"}
                                                                    animation={"wave"}
                                                                    width={150}
                                                                    height={22}
                                                                />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </MenuItem>
                                            ) : (
                                                metadataTemplate?.data?.map((option, index) => (
                                                    <MenuItem key={`metadataTemplate-${index}`} value={option.id}>
                                                        <Typography component="span" variant="body1">
                                                            {option.name}
                                                        </Typography>
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </Grid>
                                    <StyledGrid item theme={theme}>
                                        {pendingMetadata ? (
                                            <MetadataSkeletonCreateAsset />
                                        ) : (
                                            <>
                                                <Grid container alignItems={"center"} mb={2.5}>
                                                    <Grid
                                                        item
                                                        sx={{ alignSelf: "flex-start", minWidth: "244px" }}
                                                        alignItems={"center"}
                                                    >
                                                        <Typography variant={"body1"} component={"p"} mt={"10px"}>
                                                            {t(`common::label::Categories`, `Categories`)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        sx={{
                                                            flex: 1,
                                                            alignSelf: "flex-start",
                                                            maxWidth: "1000px",
                                                        }}
                                                    >
                                                        <Categories
                                                            tooltipDisabled={categoryGroupTooltipDisabled}
                                                            categoryGroups={true}
                                                            selectedCategoryGroup={{
                                                                id: categoryGroup?.id,
                                                                label: categoryGroup?.name,
                                                            }}
                                                            placeholder={t(`common::label::${"Select category"}`)}
                                                            type={CATEGORY_TYPES.ASSET}
                                                            selectedValues={createAsset.data.categories?.map(
                                                                (category) => ({
                                                                    label: category.name,
                                                                    name: category.name,
                                                                    value: category.id,
                                                                    id: category.id,
                                                                }),
                                                            )}
                                                            onChange={(options) => {
                                                                onChangeCategories(options);
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid container alignItems={"center"} mb={2.5}>
                                                    <Grid
                                                        item
                                                        sx={{ alignSelf: "flex-start", minWidth: "244px" }}
                                                        alignItems={"center"}
                                                    >
                                                        <Typography variant={"body1"} component={"p"} mt={"10px"}>
                                                            {t(`common::label::Attributions`, `Attributions`)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        sx={{
                                                            flex: 1,
                                                            alignSelf: "flex-start",
                                                            maxWidth: "1000px",
                                                        }}
                                                    >
                                                        <AttributionInputArea
                                                            values={createAsset.data.attributions}
                                                            onChangeKey={(id, value, validationError) => {
                                                                onChangeAttributionKey(id, value, validationError);
                                                            }}
                                                            onChangeValue={(id, e, validationError) => {
                                                                onChangeAttributionValue(id, e, validationError);
                                                            }}
                                                            onClickAddAttribution={(id, validationError) => {
                                                                onClickAddAttribution(id, validationError);
                                                            }}
                                                            onClickRemoveAttribution={(id) => {
                                                                onClickRemoveAttribution(id);
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid container alignItems={"center"}>
                                                    <Grid
                                                        item
                                                        sx={{ alignSelf: "flex-start", minWidth: "244px" }}
                                                        alignItems={"center"}
                                                    >
                                                        <Typography variant={"body1"} component={"p"}>
                                                            {t(`common::label::Tags`, `Tags`)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        sx={{
                                                            flex: 1,
                                                            alignSelf: "flex-start",
                                                            maxWidth: "1000px",
                                                        }}
                                                    >
                                                        <TagsInput
                                                            tags={createAsset.data?.tags?.map((tag) => {
                                                                return {
                                                                    value: tag,
                                                                };
                                                            })}
                                                            isEdit={false}
                                                            disabled={pendingCreate}
                                                            onDeleteTag={(index) => {
                                                                // setTags(tags.filter((t, idx) => idx !== index));
                                                                const tags = createAsset.data?.tags?.map((tag) => {
                                                                    return {
                                                                        value: tag,
                                                                    };
                                                                });
                                                                onChangeTags(
                                                                    createAsset.data?.tags.filter(
                                                                        (t, idx) => idx !== index,
                                                                    ),
                                                                );
                                                            }}
                                                            onAddTag={(tag) => {
                                                                const newTag = tag.value;
                                                                onChangeTags([
                                                                    ...(createAsset.data?.tags || []),
                                                                    newTag,
                                                                ]);
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Divider sx={{ mt: 1.5, mb: 2.5 }} />
                                                <Grid container alignItems={"center"}>
                                                    <Grid
                                                        item
                                                        sx={{ alignSelf: "flex-start", minWidth: "244px" }}
                                                        alignItems={"center"}
                                                    >
                                                        <Typography variant={"body1"} component={"p"}>
                                                            {t(`common::label::Description`, `Description`)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        sx={{
                                                            flex: 1,
                                                            alignSelf: "flex-start",
                                                            maxWidth: "1000px",
                                                        }}
                                                    >
                                                        <TextField
                                                            fullWidth
                                                            type={"text"}
                                                            multiline={true}
                                                            maxLength={LIMIT_CHAR.description}
                                                            length={(createAsset.data.description || '').length}
                                                            defaultValue={createAsset.data.description ?? ""} //Note: defaultValue 사용이유: value사용시 한글 입력하면 단어들이 분리되서 들어감
                                                            onChange={(e) => {
                                                                onChangeDescription(e.target.value);
                                                            }}
                                                            placeholder={t(
                                                                `common::label::Please enter a description of the asset.`,
                                                                `Please enter a description of the asset.`,
                                                            )}
                                                            error={createAsset.validationErrors?.description?.level}
                                                            helperText={
                                                                createAsset.validationErrors?.description?.message
                                                                    ? t(
                                                                          `common::msg::${!!createAsset.validationErrors
                                                                              ?.description?.message}`,
                                                                          createAsset.validationErrors?.description
                                                                              ?.message,
                                                                      )
                                                                    : ""
                                                            }
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </>
                                        )}
                                    </StyledGrid>
                                </Grid>

                                {/*Custom Metadata 영역*/}
                                {compareVersions(stage.version, VERSION_CM_11209_ADD_CUSTOM_METADATA) >= 0 &&
                                    !domainStatus.CUSTOM_CONTENTS_OFF && (
                                        <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                            <Grid container flexDirection={"row"} alignItems={"center"}>
                                                <Grid item flex={1}>
                                                    <Typography
                                                        variant={`body1`}
                                                        component={"p"}
                                                        color={theme.palette.secondary[800]}
                                                    >
                                                        {t(`common::label::Custom Metadata`)}
                                                    </Typography>
                                                </Grid>
                                                <Select
                                                    variant={"filled"}
                                                    displayEmpty
                                                    onChange={(e) => loadDetailCustomMetadata(e.target.value)}
                                                    sx={{
                                                        backgroundColor: theme.palette.common.white,
                                                    }}
                                                    value={createAsset.data.contentSchemaMetadata?.id ?? null}
                                                    renderValue={() => {
                                                        const selected = createAsset.data.contentSchemaMetadata?.id;
                                                        if (!selected) {
                                                            return (
                                                                <Grid
                                                                    container
                                                                    flexDirection={"row"}
                                                                    gap={1}
                                                                    alignItems="center"
                                                                >
                                                                    <IconSchema size={16} />
                                                                    <Typography
                                                                        variant={"body1"}
                                                                        color={theme.palette.secondary[800]}
                                                                    >
                                                                        {t(
                                                                            `common::label::Load custom metadata schema`,
                                                                        )}
                                                                    </Typography>
                                                                </Grid>
                                                            );
                                                        }

                                                        const selectedTemplate =
                                                            customMetadataSchema?.data?.results?.find(
                                                                (template) => template.id === selected,
                                                            );

                                                        return (
                                                            <Grid
                                                                container
                                                                flexDirection={"row"}
                                                                gap={1}
                                                                alignItems="center"
                                                            >
                                                                <IconMetadata
                                                                    size={16}
                                                                    color={theme.palette.secondary[700]}
                                                                />
                                                                <Typography variant="body1">
                                                                    {selectedTemplate?.name}
                                                                </Typography>
                                                            </Grid>
                                                        );
                                                    }}
                                                >
                                                    {customMetadataSchema?.pending ? (
                                                        <MenuItem>
                                                            <Grid
                                                                container
                                                                flexDirection={"column"}
                                                                px={1.5}
                                                                py={0.5}
                                                                gap={1}
                                                            >
                                                                {new Array(3).fill("").map((_, i) => (
                                                                    <Grid
                                                                        item
                                                                        key={`custom-metadata-schema-skeleton-${i}`}
                                                                    >
                                                                        <Skeleton
                                                                            variant={"rounded"}
                                                                            animation={"wave"}
                                                                            width={150}
                                                                            height={22}
                                                                        />
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </MenuItem>
                                                    ) : (
                                                        customMetadataSchema?.data.results?.map((option, index) => (
                                                            <MenuItem
                                                                key={`metadataTemplate-${index}`}
                                                                value={option.id}
                                                            >
                                                                <Typography component="span" variant="body1">
                                                                    {option.name}
                                                                </Typography>
                                                            </MenuItem>
                                                        ))
                                                    )}
                                                </Select>
                                            </Grid>
                                            <StyledGrid item theme={theme}>
                                                {customMetadataSchema.pending ? (
                                                    <MetadataSkeletonCreateAsset />
                                                ) : (
                                                    <>
                                                        <Grid container flexDirection={"row"} alignItems={"center"}>
                                                            <Grid item flex={1}>
                                                                {assetCustomMetadata?.length > 0 ? (
                                                                    <Stack
                                                                        direction={"row"}
                                                                        spacing={1}
                                                                        alignItems={"center"}
                                                                    >
                                                                        <IconSchema size={16} color={"success"} />
                                                                        <Typography
                                                                            variant={"body1_500"}
                                                                            color={theme.palette.greySecondary.main}
                                                                        >
                                                                            {
                                                                                createAsset.data.contentSchemaMetadata
                                                                                    ?.name
                                                                            }
                                                                        </Typography>
                                                                    </Stack>
                                                                ) : (
                                                                    <Typography
                                                                        color={theme.palette.greySecondary[400]}
                                                                    >
                                                                        {t(
                                                                            "common::label::Please select a custom metadata preset.",
                                                                        )}
                                                                    </Typography>
                                                                )}
                                                            </Grid>
                                                            {shouldShowUasLinkButton() && (
                                                                <Grid item sx={{ mr: theme.spacing(2.5) }}>
                                                                    <Button
                                                                        size={"xsmall"}
                                                                        variant={"contained"}
                                                                        color={"greySecondary"}
                                                                        endIcon={
                                                                            <IconOpenInNew
                                                                                size={16}
                                                                                htmlColor={theme.palette.common.white}
                                                                            />
                                                                        }
                                                                        onClick={() =>
                                                                            window.open(
                                                                                "http://guas.lxhausys.com/cm/service/html.ncd?view=/lgerp/pd/retrievePDList&topId",
                                                                                "_blank",
                                                                            )
                                                                        }
                                                                    >
                                                                        UAS 품의서 조회
                                                                    </Button>
                                                                </Grid>
                                                            )}
                                                            <Grid item>
                                                                <Tooltip title={t(`common::label::Delete`)}>
                                                                    <IconButton
                                                                        size="xsmall"
                                                                        circled
                                                                        disabled={
                                                                            !assetCustomMetadata ||
                                                                            assetCustomMetadata?.length === 0
                                                                        }
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            initContentSchemaMetadata(null);
                                                                        }}
                                                                    >
                                                                        <IconCancel
                                                                            size={12}
                                                                            htmlColor={theme.palette.grey[500]}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Grid>
                                                            <Divider
                                                                orientation={"vertical"}
                                                                flexItem={true}
                                                                sx={{ mr: 1, ml: 1.5 }}
                                                            />
                                                            <Grid item>
                                                                <Button
                                                                    disabled={
                                                                        !assetCustomMetadata ||
                                                                        assetCustomMetadata?.length === 0
                                                                    }
                                                                    size={"small"}
                                                                    variant={"text"}
                                                                    startIcon={<IconResetSmall size={16} />}
                                                                    color={"greySecondary"}
                                                                    onClick={() => {
                                                                        initialCustomMetadata ||
                                                                        customMetadataSchema.defaultCustomMetadataId
                                                                            ? loadDetailCustomMetadata(
                                                                                  initialCustomMetadata?.customMetadataId ??
                                                                                      customMetadataSchema.defaultCustomMetadataId,
                                                                              )
                                                                            : initContentSchemaMetadata(null);
                                                                    }}
                                                                >
                                                                    <Typography variant={"body3"}>
                                                                        {t(`common::label::Reset`)}
                                                                    </Typography>
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                        <Divider flexItem={true} sx={{ m: "20px 0" }} />
                                                        {assetCustomMetadata?.map((field) => (
                                                            <Grid
                                                                key={`metadata-field-${field.id}-wrapper`}
                                                                container
                                                                alignItems={"center"}
                                                                mb={2.5}
                                                            >
                                                                <Grid
                                                                    item
                                                                    sx={{ alignSelf: "flex-start", minWidth: "244px" }}
                                                                    alignItems={"center"}
                                                                    mt={theme.spacing(1)}
                                                                >
                                                                    <PanelPropertyTitle
                                                                        variant={"body1"}
                                                                        subtitle={field.subtitle}
                                                                        required={field.isRequired}
                                                                        tooltip={field.description}
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    sx={{
                                                                        flex: 1,
                                                                        alignSelf: "flex-start",
                                                                    }}
                                                                >
                                                                    <MetadataFieldEditMode
                                                                        id={field.id}
                                                                        type={field.type}
                                                                        value={field.value}
                                                                        options={field.options}
                                                                        disabled={pendingCreate || !field.isEditable}
                                                                        validationRule={field.validationRule}
                                                                        onChangeField={field.onChangeField}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                    </>
                                                )}
                                            </StyledGrid>
                                        </Grid>
                                    )}
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <VersionedComponent version={VERSION_CM_14175_ASSET_LOCK_WHILE_CREATE}>
                        <Stack flex={1} direction={"row"}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={createAsset.data.isOwnerLocked}
                                        onChange={() =>
                                            dispatch(updateData({ isOwnerLocked: !createAsset.data.isOwnerLocked }))
                                        }
                                        color="greySecondary"
                                        disabled={pendingCreate}
                                    />
                                }
                                label={
                                    <Typography variant="body1_400" color={theme.palette.greySecondary.main}>
                                        {t(`common::msg::Lock this asset`, `Lock this asset`)}
                                    </Typography>
                                }
                                disabled={pendingCreate}
                                sx={{ m: 0 }}
                            />
                        </Stack>
                    </VersionedComponent>
                    <Box>
                        <Button
                            color="secondary"
                            onClick={() => onHandleCloseAddAsset()}
                            variant="text"
                            disabled={pendingCreate}
                        >
                            {t(`common::label::Cancel`, `Cancel`)}
                        </Button>
                        <Button
                            color="primary"
                            onClick={onClickCreateAsset}
                            variant="contained"
                            loading={pendingCreate}
                            disabled={
                                pendingCreate ||
                                customMetaDataRequiredError ||
                                Object.keys(createAsset.selectedFiles).length === 0 ||
                                Object.keys(createAsset.validationErrors).length > 0
                            }
                        >
                            {t(`common::label::Create`, `Create`)}
                        </Button>
                    </Box>
                </DialogActions>
            </StickyHeaderDialog>
        </>
    );
};

export default DialogCreateAsset;
